import { useMsal } from "@azure/msal-react";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, Transition, Dialog, TransitionChild, DialogPanel, Switch } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState, useMemo, useEffect } from "react";
import { Fragment } from "react/jsx-runtime";
import { WidgetDbDemoModel } from './App.model';
import { EmfTargetConfigDbModel, EmfWidgetDbModel, EmfWidgetConfig, EmfTargetMappingModel } from "./components/widget/widget.models";
import { WidgetService } from "./components/widget/widget.service";
import { WidgetTruncatedText, WidgetFilterMenu, WidgetRefreshMenu, WidgetSettingsMenu, WidgetCollapseMenu, WidgetVisualizationMenu } from "./components/widget/widget.utils";
import EmfDislikeIcon from './components/widget/icons/dislike';
import EmfFavouriteIcon from './components/widget/icons/favourite';
import EmfLikeIcon from './components/widget/icons/like';

const TargetSelectionDropdown = ({ list, selectedItem, onSelectionChange }) => {

    if (!list?.length) return;

    const targets = list as EmfTargetMappingModel[];

    let selectedOne = targets.find(t => t.target == selectedItem?.target);
    if (!selectedOne) { selectedOne = targets[0]; }

    const [selected, setSelected] = useState(selectedOne);

    useEffect(() => {
        onSelectionChange(selected);
    }, [selected]);

    return (
        <div className="emf-w-full emf-min-w-sm emf-overflow-visible">        
            <Listbox value={selected} onChange={setSelected}>
                <ListboxButton style={{ border: '1px solid #d1d5db' }} className="emf-relative emf-w-full emf-max-w-sm emf-cursor-pointer emf-rounded emf-bg-white emf-p-2 emf-px-4 emf-text-left focus:emf-outline-none emf-text-gray-700 emf-font-medium">
                    {selected.domain}
                    <ChevronDownIcon className="emf-group emf-pointer-events-none emf-absolute emf-top-2.5 emf-right-2.5 emf-size-4 emf-fill-white/60" aria-hidden="true" />
                </ListboxButton>
                <ListboxOptions className="emf-absolute emf-mt-1 emf-z-10 emf-max-h-60 emf-w-full emf-max-w-sm emf-overflow-auto emf-rounded-md emf-bg-white emf-py-1 emf-text-base emf-shadow emf-ring-1 emf-ring-black emf-ring-opacity-5 focus:emf-outline-none">
                    {targets.map((option) => (
                        <ListboxOption
                            key={option.domain}
                            value={option}
                            className={({ active }) => `emf-relative emf-cursor-pointer emf-select-none emf-p-2 emf-px-4 ${active ? 'emf-bg-blue-100 emf-text-blue-900' : 'emf-text-gray-900'}`}>
                            {({ selected }) => (
                                <span className={`emf-block emf-text-gray-700 emf-font-medium emf-text-sm emf-truncate ${selected ? 'emf-font-medium' : 'emf-font-normal'}`}>
                                    {option.domain}
                                </span>
                            )}
                        </ListboxOption>
                    ))}
                </ListboxOptions>
            </Listbox>
        </div>
    );
};

const PreferenceSwitch = ({ selectedVal, onChange }) => {

    const [enabled, setEnabled] = useState(selectedVal);

    const handleSwitchChange = (value: boolean) => {
        setEnabled(value);
        onChange(value);
    };

    return (
        <Switch
            checked={enabled}
            onChange={handleSwitchChange}
            style={{ backgroundColor: enabled ? '#a38300' : '' }}
            className={`emf-relative emf-p-0 emf-inline-flex emf-h-5 emf-w-10 emf-shrink-0 emf-cursor-pointer emf-rounded-full emf-border-2 emf-border-transparent emf-transition-colors emf-duration-200 emf-ease-in-out ${enabled ? '' : 'emf-bg-gray-300'}`}>
            <span className={`emf-inline-block emf-h-4 emf-w-4 emf-transform emf-rounded-full emf-bg-white emf-shadow emf-ring-0 emf-transition emf-duration-200 emf-ease-in-out ${enabled ? 'emf-translate-x-5' : 'emf-translate-x-0'}`} />
        </Switch>
    );
};

const PreferencePositionDropdown = ({ selectedVal, onChange }) => {

    const [selectedValue, setSelectedValue] = useState(selectedVal);

    const handleChange = (event) => {
        setSelectedValue(event.target.value);
        onChange(event.target.value);
    };

    return (
        <select className='emf-max-w-fit' value={selectedValue} onChange={handleChange}>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
        </select>
    );
};

const preferenceJsx = () => {
    return (
        <>
            <EmfFavouriteIcon title="Favourite" className={`emf-widget-action-icon hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary`} />
            <EmfLikeIcon title="Like" className={`emf-widget-action-icon hover:emf-text-primary  focus:emf-outline-none`} />
            <EmfDislikeIcon title="Dislike" className={` emf-widget-action-icon  hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary`} />
        </>
    )
}

const AdministrationComponent = () => {

    const { instance } = useMsal();
    const widgetService = useMemo(() => WidgetService.getInstance(), []);
    const demoAccessKey = process.env.REACT_APP_DEMO_ACCESS_KEY;

    const [authorized, setAuthorized] = useState(false);
    const [targetMappingList, setTargetMappingList] = useState<EmfTargetMappingModel[]>([]);
    const [targetMapping, setTargetMapping] = useState<EmfTargetMappingModel>();
    const [targetConfigDetail, setTargetConfigDetail] = useState<EmfTargetConfigDbModel>();
    const [widgets, setWidgets] = useState<EmfWidgetDbModel[]>();
    const [widgetToEdit, setWidgetToEdit] = useState<EmfWidgetDbModel>();
    const [showPopup, setShowPopup] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const allowedAdminAccounts = ['Aditya.3.Singh@Coforge.com', 'Inder.Singh@Coforge.com', 'Aditya.3.Singh@advantagego.com', 'Inder.Singh@advantagego.com']
        const activeAccount = instance.getActiveAccount();
        if (activeAccount && allowedAdminAccounts.some(s => s.toLowerCase() == activeAccount.username?.toLowerCase())) {
            setAuthorized(true);
        }
    }, []);

    useEffect(() => {
        if (!authorized) { return; }
        const config = new EmfWidgetConfig();
        config.accessKey = demoAccessKey;
        setLoading(true);
        widgetService.getWidgets(config).then(res => {
            if (res.data) {
                let list: WidgetDbDemoModel[] = [];
                res.data.forEach(d => list.push(new WidgetDbDemoModel(d)));
                setWidgets(list);
            }
        }).catch(err => {
            setLoading(false);
        }).finally(() => {
            setLoading(false);
        })
    }, [authorized])

    useEffect(() => {
        if (!authorized) { return; }
        const config = new EmfWidgetConfig();
        config.accessKey = demoAccessKey;
        widgetService.getTargetMappingList(config).then(res => {
            if (res.data) {
                setTargetMappingList(res.data);
            }
        }).catch(err => {
            setLoading(false);
        }).finally(() => {
            setLoading(false);
        })
    }, [authorized])

    useEffect(() => {
        if (!authorized || !targetMapping || !widgets?.length) { return; }
        const config = new EmfWidgetConfig();
        config.accessKey = demoAccessKey;
        if (targetConfigDetail?.domain == targetMapping.domain) { return; }
        setLoading(true);
        widgetService.getTargetConfigDetails(config, targetMapping.domain).then(res => {
            if (res && res.data) {
                const targetConfigData = new EmfTargetConfigDbModel();
                targetConfigData.domain = res.data.domain;
                targetConfigData.id = res.data.id;
                targetConfigData.target = res.data.target;
                targetConfigData.accessKey = res.data.accessKey;
                res.data.widgets.forEach(wd => {
                    const dbWidgetItem = widgets.find(f => f.id == wd.id);
                    if (dbWidgetItem) {
                        const w = new EmfWidgetDbModel();
                        w.id = dbWidgetItem.id;
                        w.type = dbWidgetItem.type
                        w.name = dbWidgetItem.name;
                        w.category = dbWidgetItem.category;
                        w.description = dbWidgetItem.description;
                        w.icon = dbWidgetItem.icon;
                        w.title = dbWidgetItem.title;
                        w.params = dbWidgetItem.params;
                        w.settings = dbWidgetItem.settings;
                        w.subTitle = dbWidgetItem.subTitle;
                        w.order = dbWidgetItem.order;
                        w.config = wd.config;
                        targetConfigData.widgets.push(w);
                    }
                });
                setTargetConfigDetail(targetConfigData);
                setLoading(false);
            }
        }).catch(err => {
            setLoading(false);
        })
    }, [targetMapping])

    const onTargetValueChange = (data: EmfTargetMappingModel) => {
        setTargetMapping(data);
    }

    const onConfigureWidgetClick = (widget: EmfWidgetDbModel) => {
        setWidgetToEdit(widget);
        setShowPopup(true);
    }

    const WidgetEditorPopup = () => {
        if (!targetConfigDetail) return;
        if (!widgetToEdit) return;

        const [widget, setWidget] = useState<EmfWidgetDbModel>(widgetToEdit);
        const [loading, setLoading] = useState(false);

        const onControlValueChanges = (c: [string, any], val: any) => {
            widget.config[c[0]] = val;

            if (c[0] == 'preference' && val == true) {
                if (!widget.config.footer) {
                    widget.config.footer = true;
                }
            }

            setWidget({ ...widget });
        }

        const onSaveLayoutConfigClick = () => {

            const activeAccount = instance.getActiveAccount();
            const allowedAdminAccounts = ['Aditya.3.Singh@Coforge.com', 'Inder.Singh@Coforge.com', 'Aditya.3.Singh@advantagego.com', 'Inder.Singh@advantagego.com']
            if (!allowedAdminAccounts.some(s => s.toLowerCase() == activeAccount?.username?.toLowerCase())) {
                alert('Not Authorized');
                return;
            }

            setLoading(true);
            const config = new EmfWidgetConfig();
            config.accessKey = demoAccessKey;

            widgetService.saveTargetConfigDetails(config, targetMapping.domain, widget).then(res => {
                setLoading(false);
                if (res && res.data) {
                    alert('Configuration updated!')
                }
            }).catch(err => {
                setLoading(false);
                alert('Sorry! An error occurred while updating the configuration.')
            })
        }

        return (
            <Transition appear show={showPopup} as={Fragment}>
                <Dialog as="div" className="emf-relative emf-z-[1500] emf-w-full" onClose={() => { }}>
                    <TransitionChild
                        as={Fragment}
                        enter="emf-ease-out emf-duration-500"
                        enterFrom="emf-opacity-0"
                        enterTo="emf-opacity-100"
                        leave="emf-ease-in emf-duration-200"
                        leaveFrom="emf-opacity-100"
                        leaveTo="emf-opacity-0">
                        <div className="emf-fixed emf-inset-0 emf-bg-black/25" />
                    </TransitionChild>

                    <div className="emf-fixed emf-inset-0">
                        <div className="emf-flex emf-w-full emf-min-h-full emf-items-center emf-justify-center emf-text-center">
                            <TransitionChild
                                as={Fragment}
                                enter="emf-ease-out emf-duration-500"
                                enterFrom="emf-opacity-0 emf-scale-90"
                                enterTo="emf-opacity-100 emf-scale-100"
                                leave="emf-ease-in emf-duration-300"
                                leaveFrom="emf-opacity-100 emf-scale-100"
                                leaveTo="emf-opacity-0 emf-scale-90">
                                <DialogPanel className={`emf-w-4/6 emf-h-[92vh] emf-p-5 emf-transform emf-overflow-hidden emf-rounded-lg emf-align-middle emf-shadow-xl emf-transition-all emf-box-border emf-bg-white`}>
                                    <div className='emf-flex emf-flex-col emf-items-center emf-place-content-between emf-gap-5 emf-w-full emf-h-full'>

                                        <div className="emf-flex emf-items-start emf-place-content-between emf-w-full emf-grow-0">
                                            <div className="emf-flex emf-flex-col emf-items-start">
                                                <div className='emf-text-sm emf-text-left'><span className='emf-font-medium'>Target :</span> <a href={`https://${targetConfigDetail.domain}`}>{targetConfigDetail.domain}</a></div>
                                                <div className='emf-text-sm emf-text-left emf-line-clamp-2' title={widget.description}><span className='emf-font-medium'>Description :</span> <span className='emf-text-xs emf-text-left'> {widget.description}</span></div>
                                            </div>
                                            <div className="emf-flex emf-items-center">
                                                <button type="button" onClick={() => { setShowPopup(false) }} className='emf-border-none emf-bg-transparent emf-outline-none emf-w-5 emf-h-5 emf-cursor-pointer hover:emf-text-yellow-600' aria-label="Close">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        <div className='emf-flex emf-w-full emf-h-full emf-gap-5'>
                                            <div className='emf-w-3/4 emf-max-h-full'>
                                                <div className={`emf-widget emf-bg-white emf-w-full emf-rounded-lg emf-shadow-custom emf-h-full emf-flex emf-flex-col emf-place-content-between`}>

                                                    <div className={`emf-widget-container ${widget.config.padding == true ? 'emf-p-4' : 'emf-p-0'}`}>

                                                        {
                                                            widget.config.header && <div className="emf-widget-header">
                                                                <div className="emf-widget-title-container">
                                                                    <span className="emf-widget-title">
                                                                        <WidgetTruncatedText text={widget.title}></WidgetTruncatedText>
                                                                    </span>
                                                                    {
                                                                        widget.subTitle &&
                                                                        <span className="emf-widget-sub-title">
                                                                            <WidgetTruncatedText text={widget.subTitle}></WidgetTruncatedText>
                                                                        </span>
                                                                    }
                                                                </div>
                                                                <div className="emf-widget-action-container">
                                                                    {widget.config.preference && widget.config.preferencePosition == 'top' && preferenceJsx()}
                                                                    {widget.config.filter && <WidgetFilterMenu config={new EmfWidgetConfig} />}
                                                                    {widget.config.refresh && <WidgetRefreshMenu config={new EmfWidgetConfig} />}
                                                                    {widget.config.setting && <WidgetSettingsMenu config={new EmfWidgetConfig} />}
                                                                    {widget.config.expand && <WidgetCollapseMenu config={new EmfWidgetConfig} />}
                                                                    {widget.config.visualization && <WidgetVisualizationMenu config={new EmfWidgetConfig} />}
                                                                </div>
                                                            </div>
                                                        }

                                                        <div className="emf-widget-body">

                                                            <div className="emf-flex emf-items-end emf-h-full emf-mx-4">
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[78%] dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[96%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[72%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[64%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[80%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[62%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[83%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[52%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[92%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[74%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[80%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[62%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                                <div className="emf-w-full emf-bg-gray-100 emf-rounded-t-lg emf-h-[88%] emf-ms-6 dark:emf-bg-gray-700"></div>
                                                            </div>

                                                        </div>

                                                        {
                                                            widget.config.footer && <div className="emf-widget-footer emf-place-content-between">
                                                                <div className="emf-widget-action-container">
                                                                    {
                                                                        widget.config.preference
                                                                        && (widget.config.preferencePosition == 'bottom' || !widget.config.preferencePosition)
                                                                        && preferenceJsx()
                                                                    }
                                                                </div>
                                                                {
                                                                    <div className={` ${(widget.config.preference && (widget.config.preferencePosition == 'bottom' || !widget.config.preferencePosition)) ? '!emf-max-w-fit !emf-min-w-fit' : ' '}`}>

                                                                    </div>
                                                                }
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='emf-w-1/4 emf-max-h-full'>
                                                <div className="emf-flex emf-flex-col emf-place-content-between emf-h-full">

                                                    <div className='emf-flex emf-flex-col emf-gap-y-2 emf-place-content-end'>
                                                        {
                                                            Object.entries(widget.config).map(c => {
                                                                return (
                                                                    <div key={c[0]} className="emf-py-1 emf-px-3 emf-border emf-border-dashed emf-border-gray-300 emf-rounded">
                                                                        <div className="emf-flex emf-items-center emf-place-content-between emf-gap-x-5">
                                                                            <div className="emf-text-sm emf-text-gray-700 emf-truncate dark:emf-text-white emf-capitalize">
                                                                                {c[0]} {(c[1] == true || c[1] == false) ? 'enabled' : ''}
                                                                            </div>
                                                                            <div className="emf-inline-flex emf-items-center emf-text-base emf-font-semibold emf-text-gray-900 dark:emf-text-white">
                                                                                {
                                                                                    (c[1] == true || c[1] == false)
                                                                                        ? <PreferenceSwitch selectedVal={c[1]} onChange={(value: any) => { onControlValueChanges(c, value) }} />
                                                                                        : c[0].toLowerCase() == 'preferenceposition' ? <PreferencePositionDropdown selectedVal={c[1]} onChange={(value: any) => { onControlValueChanges(c, value) }} /> : c[1]
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </div>

                                                    <div className='emf-flex emf-place-content-end'>
                                                        <button type="button" disabled={loading} onClick={() => { onSaveLayoutConfigClick() }} className={`emf-border-none emf-flex emf-items-center emf-place-content-center emf-rounded-md emf-text-white emf-bg-yellow-600 emf-outline-none emf-w-44 emf-h-10 emf-cursor-pointer ${loading ? 'emf-opacity-50 emf-pointer-events-none emf-cursor-wait' : ''}`} aria-label="Close">
                                                            Save Layout Config
                                                        </button>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                        {
                                            loading &&
                                            <div role="status" className="emf-absolute -emf-translate-x-1/2 -emf-translate-y-1/2 emf-top-2/4 emf-left-1/2">
                                                <svg aria-hidden="true" className="emf-w-8 emf-h-8 emf-text-gray-200 emf-animate-spin dark:emf-text-gray-600 emf-fill-yellow-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
                                            </div>
                                        }
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        )
    }

    return (
        <>
            <nav id="navbar-main" className="navbar">
                <div className="navbar-brand">
                    <a className="navbar-item mobile-aside-button">
                        <span className="icon"><i className="mdi mdi-forwardburger mdi-24px"></i></span>
                    </a>
                    <div className="navbar-item">
                        <div className="control emf-flex emf-items-center emf-gap-10">
                            <b>Administration</b>
                            {
                                authorized && <div className="emf-flex emf-items-center emf-place-content-start emf-gap-10">

                                    <div className="emf-flex emf-items-center emf-place-content-start emf-min-w-[430px]">
                                        <div className="emf-flex emf-font-semibold emf-min-w-[64px]">Target : </div>
                                        <TargetSelectionDropdown list={targetMappingList} selectedItem={targetMapping} onSelectionChange={(val: EmfTargetMappingModel) => onTargetValueChange(val)}></TargetSelectionDropdown>
                                    </div>
                                    {
                                        targetConfigDetail && <div className='emf-text-sm emf-text-left'>
                                            <span className='emf-font-medium'>Access Key : </span>
                                            <span className='emf-text-xs emf-text-red-400'>{targetConfigDetail.accessKey}</span>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className="navbar-menu" id="navbar-menu">
                    <div className="navbar-end">
                        <div className="navbar-item dropdown has-divider">

                        </div>
                    </div>
                </div>
            </nav>
            <div className="emf-bg-gray-100 emf-w-full emf-h-full emf-flex emf-flex-col emf-flex-1">
                {!authorized && <div>Not Authorized</div>}
                {
                    authorized && <div className="emf-w-full emf-h-full emf-flex emf-flex-col emf-gap-y-5 emf-box-border emf-p-5">

                        <div className="emf-grid emf-grid-cols-3 emf-gap-5 emf-box-border">
                            {
                                targetConfigDetail && targetConfigDetail.widgets?.map((item, i) => {
                                    return (
                                        <div key={item.id} className={`emf-widget emf-bg-white emf-w-full emf-shadow-custom emf-h-[64%] emf-flex emf-flex-col emf-place-content-between`}>

                                            <div className={`emf-widget-container emf-group ${item.config.padding == true ? 'emf-p-4' : 'emf-p-0'}`}>

                                                <div onClick={() => { onConfigureWidgetClick(item) }} className='emf-cursor-pointer emf-flex emf-items-center emf-place-content-center emf-w-full emf-h-full emf-absolute emf-inset-0 emf-justify-center emf-opacity-0 group-hover:emf-opacity-100 group-hover:emf-bg-gray-50 group-hover:emf-bg-opacity-75 emf-transition-opacity'>
                                                    <button title='Configure Layout' className="emf-flex emf-items-center emf-gap-2 emf-p-4 emf-text-yellow-600  emf-rounded hover:emf-text-yellow-500 emf-outline-none emf-border-none emf-max-w-fit emf-cursor-pointer">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                                            <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                {
                                                    <div className="emf-widget-header">
                                                        <div className="emf-widget-title-container">
                                                            <span className="emf-widget-title">
                                                                <WidgetTruncatedText text={item.title}></WidgetTruncatedText>
                                                            </span>
                                                            {
                                                                item.subTitle &&
                                                                <span className="emf-widget-sub-title">
                                                                    <WidgetTruncatedText text={item.subTitle}></WidgetTruncatedText>
                                                                </span>
                                                            }
                                                        </div>
                                                        <div className="emf-widget-action-container">
                                                            {item.config.preference && item.config.preferencePosition == 'top' && preferenceJsx()}
                                                            {item.config.filter && <WidgetFilterMenu config={new EmfWidgetConfig} />}
                                                            {item.config.refresh && <WidgetRefreshMenu config={new EmfWidgetConfig} />}
                                                            {item.config.setting && <WidgetSettingsMenu config={new EmfWidgetConfig} />}
                                                            {item.config.expand && <WidgetCollapseMenu config={new EmfWidgetConfig} />}
                                                            {item.config.visualization && <WidgetVisualizationMenu config={new EmfWidgetConfig} />}
                                                        </div>
                                                    </div>
                                                }

                                                <div className="emf-widget-body">
                                                    <svg className="emf-w-full emf-h-40 emf-text-gray-200" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15v4m6-6v6m6-4v4m6-6v6M3 11l6-5 6 5 5.5-5.5" />
                                                    </svg>
                                                </div>
                                                <div className="emf-widget-footer emf-place-content-between">
                                                    <div className="emf-widget-action-container">
                                                        {
                                                            item.config.preference
                                                            && (item.config.preferencePosition == 'bottom' || !item.config.preferencePosition)
                                                            && preferenceJsx()
                                                        }
                                                    </div>
                                                    {
                                                        <div className={` ${(item.config.preference && (item.config.preferencePosition == 'bottom' || !item.config.preferencePosition)) ? '!emf-max-w-fit !emf-min-w-fit' : ' '}`}>

                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            }

                            {
                                loading && <div role="status" className="emf-absolute -emf-translate-x-1/2 -emf-translate-y-1/2 emf-top-2/4 emf-left-1/2">
                                    <svg aria-hidden="true" className="emf-w-8 emf-h-8 emf-text-gray-200 emf-animate-spin dark:emf-text-gray-600 emf-fill-yellow-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
                                </div>
                            }
                        </div>
                        {targetConfigDetail && widgetToEdit && <WidgetEditorPopup></WidgetEditorPopup>}
                    </div>
                }
            </div>
        </>
    )
}

export default AdministrationComponent;
